import type { IAssembly, IKit } from 'containers/KitWorkspaceStorage/types';
import { buildSpecLines } from 'common/components/kit-shared/assemblySummary';

/**
 * Text filter for the Manage Kits page (US-18). Matches an assembly against the
 * user's query across three surfaces:
 *  - name: explicit name, summaryData.title (part number), notes;
 *  - spec: the flattened spec lines (hose/fitting titles, length, rotation,
 *    customer part number, or the generic hydrapark spec list);
 *  - part: every attached accessory's part number + description.
 * Matching is case-insensitive substring; an empty query matches everything.
 */
const assemblyHaystack = (assembly: IAssembly): string => {
  const parts: Array<string | undefined> = [
    assembly.name,
    assembly.summaryData?.title,
    assembly.notes,
  ];

  buildSpecLines(assembly).forEach((line) => {
    parts.push(line.label, line.value);
  });

  assembly.accessories.forEach((accessory) => {
    parts.push(accessory.partNumber, accessory.description);
  });

  return parts.filter(Boolean).join(' ').toLowerCase();
};

export const normalizeQuery = (query: string): string => query.trim().toLowerCase();

export const assemblyMatches = (assembly: IAssembly, normalizedQuery: string): boolean => {
  if (!normalizedQuery) return true;

  return assemblyHaystack(assembly).includes(normalizedQuery);
};

/**
 * A kit matches when its own name/notes match, any of its assembly lines match,
 * or any of its directly-attached accessories match.
 */
export const kitMatches = (kit: IKit, normalizedQuery: string): boolean => {
  if (!normalizedQuery) return true;

  const own = [kit.name, kit.notes].filter(Boolean).join(' ').toLowerCase();
  if (own.includes(normalizedQuery)) return true;

  if (kit.lines.some((line) => assemblyMatches(line.assembly, normalizedQuery))) return true;

  const accessoryHay = kit.accessories
    .map((a) => `${a.partNumber} ${a.description ?? ''}`)
    .join(' ')
    .toLowerCase();

  return accessoryHay.includes(normalizedQuery);
};

export const filterAssemblies = (assemblies: IAssembly[], query: string): IAssembly[] => {
  const normalized = normalizeQuery(query);
  if (!normalized) return assemblies;

  return assemblies.filter((assembly) => assemblyMatches(assembly, normalized));
};

export const filterKits = (kits: IKit[], query: string): IKit[] => {
  const normalized = normalizeQuery(query);
  if (!normalized) return kits;

  return kits.filter((kit) => kitMatches(kit, normalized));
};
